import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { intersection, isEmpty } from 'lodash/fp';

import { Dropdown, FieldLabel, FormGroup } from '@portal/common/components';

import _ from '../../../locale';
import { changeInsightsFilters } from '../../../store/insights/actions';
import { VisualizationConfig } from '../../../store/insights/models';
import {
    getInsightsDataProjectId,
    getInsightsVisualizationOptions,
    getInsightsVisualizations,
} from '../../../store/insights/selectors';

export const queryVolumeKeyGetter = (config) => {
    const { type, dataType, filter } = config;
    const parts = [type, dataType, filter].filter(Boolean);
    return parts.join('_');
};

const VisualizationsFilter: React.FC = () => {
    const dispatch = useDispatch();
    const value: VisualizationConfig[] = useSelector(getInsightsVisualizations);
    const options = useSelector(getInsightsVisualizationOptions);
    const dataProjectId = useSelector(getInsightsDataProjectId);

    const handleSelect = useCallback(
        (ev) => {
            const visualizations = (ev?.target?.value || []).filter(
                ({ supportedDataProjectIds }) =>
                    isEmpty(supportedDataProjectIds) ||
                    !isEmpty(intersection(supportedDataProjectIds, dataProjectId))
            );
            dispatch(changeInsightsFilters({ visualizations }));
        },
        [dataProjectId]
    );

    const handleClear = useCallback(() => {
        dispatch(changeInsightsFilters({ visualizations: [] }));
    }, []);

    const optionLabelGetter = useCallback(({ key }) => _(`insights_visualization_${key}`), []);

    const optionDisabledGetter = useCallback(
        ({ disabled, supportedDataProjectIds }) =>
            disabled ||
            (!isEmpty(supportedDataProjectIds) &&
                isEmpty(intersection(supportedDataProjectIds, dataProjectId))),
        [dataProjectId]
    );

    return (
        <FormGroup>
            <FieldLabel>{_('insights_filter_visualizations')}</FieldLabel>
            <Dropdown
                valueMode="multiArray"
                isMulti
                isClearable
                isSearchable
                isCollapsible
                placeholder={_('insights_filter_visualizations_placeholder')}
                sortOptionsOrder="asc"
                value={value}
                options={options}
                onSelect={handleSelect}
                onMultiSelect={handleSelect}
                optionLabelGetter={optionLabelGetter}
                onClear={handleClear}
                optionDisabledGetter={optionDisabledGetter}
                onRemove={handleSelect}
            />
        </FormGroup>
    );
};

export default VisualizationsFilter;
